/**
 * Trust Calculations - Shared Data Layer
 *
 * Single source of truth for trust accounting calculations.
 * Used by both Transactions.tsx (Reconcile mode) and TrustLedgerReport.
 *
 * Trust Types:
 * - Case Trust (Matter Trust): sourceType='M' or targetType='M'
 * - Client Trust (General Trust): sourceType='G' or targetType='G'
 * - Escrow: sourceType='E' or targetType='E' (the id is the escrow bank account)
 *
 * OWA-971: getTrustLedgerEffects is the one classification of how a transaction moves trust
 * money. The Trust Ledger, Three-Way Reconciliation and IOLTA Balance reports derive their
 * balances from it, with one day-boundary rule and dated voids and refusals
 * (isTrustEffectiveAsOf), scoped to one bank account (TrustAccountScope). Money is summed with
 * the payments_wasm kernel.
 */

import {startOfDay} from 'date-fns';
import Transaction, {TxType, TxTypeType} from '@/entities/transaction';
import Matter from '@/entities/matter';
import Contact from '@/entities/contact';
import FinancialHelper from '@/Financial/FinancialHelper';
import {moneyCheckedSum} from '@/lib/payments';
import {firmCalendarDay, firmEndOfDay, firmStartOfDay} from '@/lib/firmTime';
import type {uuid} from '@/@types/schema';

/**
 * Interface for a lookup that can get contacts by ID.
 * Works with both Map<uuid, Contact> and ArrayMap<Contact>.
 */
interface ContactLookup {
    get(id: uuid): Contact | undefined;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Identifies a case (matter) with trust activity
 */
export type CaseTrustGroup = {
    matterId: uuid;
    matterName: string;
    matterCode: string;
    fullName: string;  // "Matter Name\n(Code)"
};

/**
 * Identifies a client with general trust activity
 */
export type ClientTrustGroup = {
    clientId: uuid;
    clientName: string;
};

/**
 * A single trust ledger row (one transaction)
 */
export type TrustLedgerRow = {
    transactionId: uuid;
    txDate: Date;
    txType: TxTypeType;
    txTypeName: string;
    referenceNumber: string | null;
    description: string | null;

    // Per-case trust amounts (keyed by matterId)
    caseTrustReceipts: Map<uuid, number>;
    caseTrustDisbursements: Map<uuid, number>;
    caseTrustBalances: Map<uuid, number>;

    // Per-client general trust amounts (keyed by clientId)
    clientTrustReceipts: Map<uuid, number>;
    clientTrustDisbursements: Map<uuid, number>;
    clientTrustBalances: Map<uuid, number>;
};

/**
 * Summary totals for the trust ledger
 */
export type TrustLedgerSummary = {
    // Per-case trust totals (keyed by matterId)
    caseTrustTotals: Map<uuid, {
        openingBalance: number;
        totalReceipts: number;
        totalDisbursements: number;
        closingBalance: number;
    }>;

    // Per-client trust totals (keyed by clientId)
    clientTrustTotals: Map<uuid, {
        openingBalance: number;
        totalReceipts: number;
        totalDisbursements: number;
        closingBalance: number;
    }>;

    transactionCount: number;
};

// ============================================================================
// Transaction Type Names
// ============================================================================

/**
 * Human-readable names for transaction types
 */
const TX_TYPE_NAMES: Record<string, string> = {
    [TxType.FundRetainerFromClient]: 'Fund Retainer from Client',
    [TxType.FundRetainerFromOperating]: 'Fund Retainer from Operating',
    [TxType.EscrowDeposit]: 'Escrow Deposit',
    [TxType.PaymentFromTrust]: 'Payment from Trust',
    [TxType.RefundRetainer]: 'Refund Retainer',
    [TxType.DisburseTrust]: 'Disburse Trust',
    [TxType.DisburseEscrow]: 'Disburse Escrow',
    [TxType.TrustTransfer]: 'Trust Transfer',
    [TxType.BalanceAdjustment]: 'Balance Adjustment',
    [TxType.StartingTrustAmount]: 'Starting Trust Amount',
};

/**
 * Get human-readable name for a transaction type
 */
export function getTxTypeName(txType: TxTypeType): string {
    return TX_TYPE_NAMES[txType] || txType;
}

// ============================================================================
// Shared Trust Classification (OWA-971)
// ============================================================================

/**
 * A client's general trust (G), a matter trust (M), or an escrow account (E, keyed by the
 * escrow bank account id).
 */
export type TrustLedgerType = 'G' | 'M' | 'E';

/** Money one transaction moves into (positive) or out of (negative) one trust ledger. */
export type TrustLedgerEffect = {
    ledgerType: TrustLedgerType;
    ledgerId: uuid;
    amount: number;
};

/**
 * The bank account a report covers. General and matter trust money is held in the firm's
 * trust account ('T'). Transactions do not record which trust account they used, so a trust
 * account covers all general and matter trust (the app lets a firm keep one trust account).
 * Escrow money is held in the escrow bank account that the transaction names as its escrow
 * source or target, for the case the escrow account belongs to (`matterId`).
 */
export type TrustAccountScope = {accountType: 'T'} | {accountType: 'E'; bankAccountId: uuid; matterId?: uuid | null};

/** The firm's trust (IOLTA) account: every general and matter trust ledger. */
export const IOLTA_SCOPE: TrustAccountScope = {accountType: 'T'};

const TRUST_LEDGER_TYPES: readonly string[] = ['G', 'M', 'E'];

// Every type that moves trust money. FinancialHelper.TRUST_TYPES leaves out the escrow deposit.
// Read lazily: FinancialHelper imports this module (OWA-1527), so FinancialHelper is not yet
// defined while this module's top level runs.
const trustMovementTypes = (): readonly string[] => [...FinancialHelper.TRUST_TYPES, TxType.EscrowDeposit];

/** Does this transaction type move trust or escrow money? */
export function isTrustMovement(tx: Transaction): boolean {
    return trustMovementTypes().includes(tx.txType);
}

function isTrustLedgerType(type: string | null | undefined): type is TrustLedgerType {
    return !!type && TRUST_LEDGER_TYPES.includes(type);
}

/**
 * Sum money exactly with the payments_wasm kernel. Inputs are snapped to scale 4 first, which
 * the kernel requires (the same normalization as FinancialHelper.getMoneyAllocation).
 */
export function sumTrustMoney(values: number[]): number {
    return moneyCheckedSum(new Float64Array(values.map(value => Math.round(value * 10000) / 10000)));
}

/**
 * How a transaction moves trust money: out of its trust source and into its trust target.
 * A transfer between two trust ledgers moves money out of one and into the other. Balance
 * adjustments, starting account amounts and operating payments and refunds are not trust
 * movements.
 */
export function getTrustLedgerEffects(tx: Transaction): TrustLedgerEffect[] {
    if (!isTrustMovement(tx)) return [];
    // A fund-retainer receipt split between case trusts (paymentDistribution) credits each case
    // trust with its own portion (OWA-1527: the balance engines all share this).
    if (tx.txType === TxType.FundRetainerFromClient && tx.targetType === 'M' && tx.paymentDistribution?.length > 0) {
        return tx.paymentDistribution
            .map(pd => ({ledgerType: 'M' as const, ledgerId: pd.matterId, amount: Math.abs(Number.parseFloat(pd.amount) || 0)}))
            .filter(effect => effect.amount !== 0);
    }
    // A starting trust amount keeps its sign (an opening overdraft is negative). Every other
    // movement is a positive amount whose direction comes from its source and target.
    const amount = tx.txType === TxType.StartingTrustAmount ? (tx.amount || 0) : Math.abs(tx.amount || 0);
    if (amount === 0) return [];
    const effects: TrustLedgerEffect[] = [];
    if (isTrustLedgerType(tx.sourceType) && tx.source) {
        effects.push({ledgerType: tx.sourceType, ledgerId: tx.source, amount: -amount});
    }
    if (isTrustLedgerType(tx.targetType) && tx.target) {
        effects.push({ledgerType: tx.targetType, ledgerId: tx.target, amount});
    }
    return effects;
}

// Types that move money into the trust account, and types that move money out of it. The
// escrow deposit and escrow disbursement are left out: they move escrow money, which the app
// records against an escrow account (contact to escrow and escrow to contact,
// FinancialHelper.transactionFailureReason TR20 and TR18), never against the trust account.
const TRUST_RECEIPT_TYPES: readonly string[] = [
    TxType.FundRetainerFromClient, TxType.FundRetainerFromOperating, TxType.StartingTrustAmount,
];
const TRUST_DISBURSEMENT_TYPES: readonly string[] = [
    TxType.PaymentFromTrust, TxType.RefundRetainer, TxType.DisburseTrust,
];

/**
 * Money a trust transaction moves with no client, case or escrow ledger on either side, such
 * as a payment from trust whose source is a contact. Its type still says the money entered or
 * left the trust account, so it counts in the trust account's balance. No client ledger holds
 * it, so the firm ledger and the sum of client ledgers disagree and the reconciliation shows
 * the variance. A transfer with no trust side has no direction and moves nothing. An escrow
 * deposit or disbursement that names no escrow account moves nothing either: escrow money is
 * not trust account money, and no escrow account can be credited with it.
 */
export function getUnattributedTrustMovement(tx: Transaction): number {
    if (getTrustLedgerEffects(tx).length > 0) return 0;
    const amount = tx.amount || 0;
    if (amount === 0) return 0;
    if (tx.txType === TxType.StartingTrustAmount) return amount;
    if (TRUST_RECEIPT_TYPES.includes(tx.txType)) return Math.abs(amount);
    if (TRUST_DISBURSEMENT_TYPES.includes(tx.txType)) return -Math.abs(amount);
    return 0;
}

/** The date a transaction stopped counting: its void or refusal date, whichever is earlier. */
export function getTrustReversalDate(tx: Transaction): Date | null {
    const times = [tx.voidedDate, tx.refusedDate]
        .filter((date): date is Date => !!date)
        .map(date => new Date(date).getTime());
    return times.length > 0 ? new Date(Math.min(...times)) : null;
}

/**
 * Does a transaction count at an exact instant? It is dated on or before `cutoff`, and a void or
 * refusal takes the money back out only after `cutoff` (a transaction reversed on or before the
 * cutoff does not count). A null cutoff is the live view: every transaction counts unless it was
 * ever voided or refused. Deleted transactions never count. isTrustEffectiveAsOf is this rule at
 * the end of a local day; callers that count days on the firm's clock pass that day's end.
 */
export function isTrustEffectiveBy(tx: Transaction, cutoff: Date | null): boolean {
    if (tx.status === 'D') return false;
    const reversal = getTrustReversalDate(tx);
    if (!cutoff) return !reversal;
    if (!tx.txDate || new Date(tx.txDate).getTime() > cutoff.getTime()) return false;
    return !reversal || reversal.getTime() > cutoff.getTime();
}

/**
 * The one day-boundary rule for every trust report: a transaction counts as of a date when it
 * is dated on or before the end of that day on the firm's clock (the same day getTrustAmount
 * uses, OWA-1804), whatever time of day `asOf` carries. A void or
 * refusal takes the money back out only from its own date, so a transaction voided or refused
 * after `asOf` still counts as of `asOf`. Deleted transactions never count.
 */
export function isTrustEffectiveAsOf(tx: Transaction, asOf: Date): boolean {
    return isTrustEffectiveBy(tx, firmEndOfDay(startOfDay(asOf)));
}

/** Is this ledger held in the bank account the report covers? */
export function isLedgerInScope(effect: TrustLedgerEffect, scope: TrustAccountScope): boolean {
    if (scope.accountType === 'E') return effect.ledgerType === 'E' && effect.ledgerId === scope.bankAccountId;
    return effect.ledgerType === 'G' || effect.ledgerType === 'M';
}

/**
 * Net money a transaction moves into (positive) or out of (negative) the bank account in
 * scope. A transfer between two ledgers in the same account nets to zero; a transfer from a
 * matter trust to an escrow account leaves the trust account and enters the escrow account.
 * A trust account movement with no ledger on either side counts in the trust account
 * (getUnattributedTrustMovement); an escrow movement with no escrow account counts nowhere.
 */
export function getTrustAccountNetChange(tx: Transaction, scope: TrustAccountScope): number {
    const unattributed = scope.accountType === 'T' ? getUnattributedTrustMovement(tx) : 0;
    return sumTrustMoney([
        ...getTrustLedgerEffects(tx)
            .filter(effect => isLedgerInScope(effect, scope))
            .map(effect => effect.amount),
        unattributed,
    ]);
}

/** The account's book balance at the end of `asOf`'s day. */
export function calculateTrustAccountBalance(
    transactions: Transaction[],
    asOf: Date,
    scope: TrustAccountScope
): number {
    return sumTrustMoney(transactions
        .filter(tx => isTrustEffectiveAsOf(tx, asOf))
        .map(tx => getTrustAccountNetChange(tx, scope)));
}

/**
 * One trust balance from the kernel: the sum of the effects on one ledger of every transaction
 * that counts at `cutoff` (isTrustEffectiveBy). `ledgerType` 'A' is every general and matter trust
 * ledger (the IOLTA account), optionally narrowed to one ledger id; 'E' is one escrow account (or
 * every escrow account with no id). This is the one balance engine behind
 * FinancialHelper.getTrustAmount and the Case and client trust columns (OWA-1527).
 */
export function getTrustBalance(
    transactions: Iterable<Transaction>,
    ledgerType: TrustLedgerType | 'A',
    ledgerId: uuid | null | undefined,
    cutoff: Date | null
): number {
    const amounts: number[] = [];
    for (const tx of transactions) {
        if (!isTrustEffectiveBy(tx, cutoff)) continue;
        for (const effect of getTrustLedgerEffects(tx)) {
            if (ledgerType === 'A' ? effect.ledgerType === 'E' : effect.ledgerType !== ledgerType) continue;
            if (ledgerId && effect.ledgerId !== ledgerId) continue;
            amounts.push(effect.amount);
        }
    }
    return sumTrustMoney(amounts);
}

/** What one transaction moves into (positive) or out of (negative) one ledger; ignores void state. */
export function getTrustRowAmount(tx: Transaction, ledgerType: TrustLedgerType, ledgerId: uuid): number {
    return sumTrustMoney(getTrustLedgerEffects(tx)
        .filter(effect => effect.ledgerType === ledgerType && effect.ledgerId === ledgerId)
        .map(effect => effect.amount));
}

/**
 * The end of the firm's calendar day that `date` falls on: the cutoff for the balance shown on a
 * row dated `date` (OWA-1463).
 */
export function trustRowCutoff(date: Date): Date {
    return firmEndOfDay(firmCalendarDay(new Date(date)));
}

/**
 * The balance a ledger table shows on a row dated `rowDate`: `rowsUpToHere` are the ledger's
 * transactions from the oldest through the row, and each counts unless it was voided or refused
 * on or before the row's day. A transaction reversed later still counts on earlier rows, so a past
 * row does not change when a transaction is voided afterwards.
 */
export function getTrustBalanceAtRow(
    rowsUpToHere: Iterable<Transaction>,
    ledgerType: TrustLedgerType,
    ledgerId: uuid,
    rowDate: Date
): number {
    return getTrustBalance(rowsUpToHere, ledgerType, ledgerId, trustRowCutoff(rowDate));
}

/**
 * Who escrow money is held for: the case the escrow account belongs to. For an escrow account
 * with no case on record, the transaction's case, or its client when it has none.
 */
export type EscrowBeneficiary = {type: 'M' | 'G'; id: uuid};

function getEscrowBeneficiary(tx: Transaction, scope: TrustAccountScope): EscrowBeneficiary | null {
    if (scope.accountType === 'E' && scope.matterId) return {type: 'M', id: scope.matterId};
    const matterId = tx.matterIds?.[0]
        ?? (tx.sourceType === 'M' ? tx.source : tx.targetType === 'M' ? tx.target : null);
    if (matterId) return {type: 'M', id: matterId};
    const clientId = (tx.sourceType === 'G' ? tx.source : tx.targetType === 'G' ? tx.target : null)
        ?? tx.clientIds?.[0];
    return clientId ? {type: 'G', id: clientId} : null;
}

/** One trust ledger's balance in the account in scope. */
export type TrustLedgerBalance = {
    ledgerType: TrustLedgerType;
    ledgerId: uuid;
    heldFor: EscrowBeneficiary | null;  // Escrow ledgers only
    balance: number;
};

/**
 * Every ledger in the account in scope with its balance at the end of `asOf`'s day, including
 * ledgers whose matter is deleted or missing. Escrow money is split by who it is held for.
 * The balances sum to calculateTrustAccountBalance for the same inputs, less any trust
 * movement that no ledger holds (getUnattributedTrustMovement).
 */
export function calculateTrustLedgerBalances(
    transactions: Transaction[],
    asOf: Date,
    scope: TrustAccountScope
): TrustLedgerBalance[] {
    const ledgers = new Map<string, {ledger: Omit<TrustLedgerBalance, 'balance'>; amounts: number[]}>();
    for (const tx of transactions) {
        if (!isTrustEffectiveAsOf(tx, asOf)) continue;
        for (const effect of getTrustLedgerEffects(tx)) {
            if (!isLedgerInScope(effect, scope)) continue;
            const heldFor = effect.ledgerType === 'E' ? getEscrowBeneficiary(tx, scope) : null;
            const key = [effect.ledgerType, effect.ledgerId, heldFor?.type ?? '', heldFor?.id ?? ''].join('|');
            const entry = ledgers.get(key)
                ?? {ledger: {ledgerType: effect.ledgerType, ledgerId: effect.ledgerId, heldFor}, amounts: []};
            entry.amounts.push(effect.amount);
            ledgers.set(key, entry);
        }
    }
    return [...ledgers.values()].map(({ledger, amounts}) => ({...ledger, balance: sumTrustMoney(amounts)}));
}

/** A dated change to the account in scope: a transaction, or the void or refusal that reverses it. */
export type TrustAccountEvent = {
    date: Date;
    amount: number;
    transactionId: uuid;
    isReversal: boolean;
};

/**
 * The account's history as dated events, oldest first. A void or refusal is its own event on
 * its own date, so earlier periods keep the money it reverses. A transaction reversed on or
 * before its own day never counts, the same as isTrustEffectiveAsOf. Summing the events dated
 * on or before a day gives calculateTrustAccountBalance for that day.
 */
export function getTrustAccountEvents(transactions: Transaction[], scope: TrustAccountScope): TrustAccountEvent[] {
    const events: TrustAccountEvent[] = [];
    for (const tx of transactions) {
        if (tx.status === 'D' || !tx.txDate) continue;
        const amount = getTrustAccountNetChange(tx, scope);
        if (amount === 0) continue;
        const txDate = new Date(tx.txDate);
        const reversal = getTrustReversalDate(tx);
        if (reversal && firmCalendarDay(reversal).getTime() <= firmCalendarDay(txDate).getTime()) continue;
        events.push({date: txDate, amount, transactionId: tx.transactionId, isReversal: false});
        if (reversal) events.push({date: reversal, amount: -amount, transactionId: tx.transactionId, isReversal: true});
    }
    return events.sort((a, b) => a.date.getTime() - b.date.getTime());
}

// ============================================================================
// Trust Transaction Filtering
// ============================================================================

/**
 * Check if a transaction affects a specific case's trust account.
 */
export function txAffectsCaseTrust(tx: Transaction, matterId: uuid): boolean {
    if (!FinancialHelper.isTrustTransaction(tx)) return false;
    // OWA-1804: the kernel's effects name the Case trusts a transaction moves, so a receipt split
    // across Cases (paymentDistribution) reaches every Case it credits, not only its target.
    return getTrustLedgerEffects(tx).some(effect => effect.ledgerType === 'M' && effect.ledgerId === matterId);
}

/**
 * Check if a transaction affects a specific client's general trust account.
 */
export function txAffectsClientTrust(tx: Transaction, clientId: uuid): boolean {
    if (!FinancialHelper.isTrustTransaction(tx)) return false;
    return getTrustLedgerEffects(tx).some(effect => effect.ledgerType === 'G' && effect.ledgerId === clientId);
}

/**
 * The cases and clients with trust activity: every matter trust and general trust that a trust
 * transaction moves money into or out of, the same rule getCaseTrustGroupsForClient and
 * getClientTrustGroupsForClient use to list a ledger.
 */
export function getTrustLedgerHolders(transactions: Transaction[]): {matterIds: Set<uuid>; clientIds: Set<uuid>} {
    const matterIds = new Set<uuid>();
    const clientIds = new Set<uuid>();
    for (const tx of filterTrustTransactions(transactions)) {
        for (const effect of getTrustLedgerEffects(tx)) {
            if (effect.ledgerType === 'M') matterIds.add(effect.ledgerId);
            else if (effect.ledgerType === 'G') clientIds.add(effect.ledgerId);
        }
    }
    return {matterIds, clientIds};
}

/**
 * What a trust transaction moves in the trust journals: the net of its ledger effects (so a split
 * receipt, a negative starting amount and a payment from a Case trust all follow the kernel), or
 * the unattributed movement when no ledger is named. Positive is money into trust, negative is
 * money out. OWA-1804.
 */
export function getJournalMovement(tx: Transaction): number {
    const effects = getTrustLedgerEffects(tx);
    if (effects.length === 0) return getUnattributedTrustMovement(tx);
    return sumTrustMoney(effects.map(effect => effect.amount));
}

/**
 * One dated line of a trust journal. A void or refusal is its own line on the day it happened with
 * the amount reversed (ABA Model Rule 1.15, ADR 0002), so a closed period never changes and each
 * period's closing figure is the next period's opening figure.
 */
export type JournalEvent = {
    tx: Transaction;
    date: Date;
    amount: number;  // In the journal's own direction; negative on a reversal line
    reversal: 'void' | 'refusal' | null;
};

/**
 * The dated lines of a trust journal: every trust movement `include` accepts, in `direction`
 * (1 for the receipts journal, -1 for the disbursements journal), then the reversal line of any
 * that was voided or refused. A transaction reversed on or before its own day (on the firm's
 * clock) never counted, so it has no lines. Deleted transactions and movements of zero have none.
 */
export function getJournalEvents(
    transactions: Iterable<Transaction>,
    include: (tx: Transaction) => boolean,
    direction: 1 | -1
): JournalEvent[] {
    const events: JournalEvent[] = [];
    for (const tx of transactions) {
        if (!isTrustMovement(tx) || tx.status === 'D' || !tx.txDate || !include(tx)) continue;
        const amount = direction * getJournalMovement(tx);
        if (amount === 0) continue;
        const date = new Date(tx.txDate);
        const reversal = getTrustReversalDate(tx);
        if (reversal && firmCalendarDay(reversal).getTime() <= firmCalendarDay(date).getTime()) continue;
        events.push({tx, date, amount, reversal: null});
        if (reversal) {
            const voided = tx.voidedDate ? new Date(tx.voidedDate).getTime() : Infinity;
            events.push({tx, date: reversal, amount: -amount, reversal: reversal.getTime() === voided ? 'void' : 'refusal'});
        }
    }
    return events;
}

/**
 * The description of a journal line: the transaction's notes, and for a reversal line which kind
 * of reversal it is.
 */
export function describeJournalLine(tx: Transaction, reversal: 'void' | 'refusal' | null): string | null {
    if (!reversal) return tx.notes ?? null;
    const label = reversal === 'void' ? 'Voided' : 'Refused';
    return tx.notes ? `${label}: ${tx.notes}` : label;
}

/**
 * A journal period as instants on the firm's clock: the first instant of the start day and the
 * last of the end day (the same day getTrustAmount counts). A missing date is no bound.
 */
export function journalPeriod(startDate?: Date | null, endDate?: Date | null): {from: Date | null; to: Date | null} {
    return {
        from: startDate ? firmStartOfDay(startOfDay(startDate)) : null,
        to: endDate ? firmEndOfDay(startOfDay(endDate)) : null,
    };
}

/**
 * Check if a transaction is voided or refused.
 */
export function isVoidedOrRefused(tx: Transaction): boolean {
    return !!(tx.voidedDate || tx.refusedDate);
}

/**
 * Filter transactions to only trust transactions, excluding voided/refused/deleted.
 */
export function filterTrustTransactions(transactions: Transaction[]): Transaction[] {
    return transactions.filter(tx =>
        FinancialHelper.isTrustTransaction(tx) &&
        tx.status !== 'D' &&
        !tx.voidedDate &&
        !tx.refusedDate
    );
}

// ============================================================================
// Case Trust Calculations
// ============================================================================

/**
 * Money a transaction moves into (direction 1) or out of (direction -1) one ledger, as a positive
 * amount. Voided and refused transactions move nothing here: the ledger reports show the
 * history as it stands today.
 */
function sumLedgerFlow(tx: Transaction, ledgerType: TrustLedgerType, ledgerId: uuid, direction: 1 | -1): number {
    if (isVoidedOrRefused(tx)) return 0;
    return sumTrustMoney(getTrustLedgerEffects(tx)
        .filter(effect => effect.ledgerType === ledgerType && effect.ledgerId === ledgerId && Math.sign(effect.amount) === direction)
        .map(effect => Math.abs(effect.amount)));
}

/**
 * Calculate case trust receipts (money IN) for a specific transaction and matter.
 * Returns the amount if money goes INTO the matter's trust account, 0 otherwise.
 */
export function calculateCaseTrustReceipts(tx: Transaction, matterId: uuid): number {
    return sumLedgerFlow(tx, 'M', matterId, 1);
}

/**
 * Calculate case trust disbursements (money OUT) for a specific transaction and matter.
 * Returns the amount if money goes OUT OF the matter's trust account, 0 otherwise.
 */
export function calculateCaseTrustDisbursements(tx: Transaction, matterId: uuid): number {
    return sumLedgerFlow(tx, 'M', matterId, -1);
}

/**
 * Calculate the net change to a case trust from a single transaction.
 * Positive = money in, Negative = money out.
 */
export function calculateCaseTrustNetChange(tx: Transaction, matterId: uuid): number {
    const receipts = calculateCaseTrustReceipts(tx, matterId);
    const disbursements = calculateCaseTrustDisbursements(tx, matterId);
    return sumTrustMoney([receipts, -disbursements]);
}

// ============================================================================
// Client Trust Calculations
// ============================================================================

/**
 * Calculate client trust receipts (money IN) for a specific transaction and client.
 * Returns the amount if money goes INTO the client's general trust account, 0 otherwise.
 */
export function calculateClientTrustReceipts(tx: Transaction, clientId: uuid): number {
    return sumLedgerFlow(tx, 'G', clientId, 1);
}

/**
 * Calculate client trust disbursements (money OUT) for a specific transaction and client.
 * Returns the amount if money goes OUT OF the client's general trust account, 0 otherwise.
 */
export function calculateClientTrustDisbursements(tx: Transaction, clientId: uuid): number {
    return sumLedgerFlow(tx, 'G', clientId, -1);
}

/**
 * Calculate the net change to a client trust from a single transaction.
 * Positive = money in, Negative = money out.
 */
export function calculateClientTrustNetChange(tx: Transaction, clientId: uuid): number {
    const receipts = calculateClientTrustReceipts(tx, clientId);
    const disbursements = calculateClientTrustDisbursements(tx, clientId);
    return sumTrustMoney([receipts, -disbursements]);
}

// ============================================================================
// Group Identification
// ============================================================================

/**
 * Find all cases (matters) that have trust activity for a given client.
 * Returns cases where the client is involved AND there are trust transactions.
 */
export function getCaseTrustGroupsForClient(
    clientId: uuid,
    matters: Matter[],
    transactions: Transaction[],
    contacts: ContactLookup
): CaseTrustGroup[] {
    // Get all matters where this client is involved. OWA-971: a deleted matter stays when it
    // has trust activity, because deleting the record does not move the money.
    const clientMatters = matters.filter(m => m.clients.includes(clientId));

    // Filter to matters that have trust transactions
    const trustTxs = filterTrustTransactions(transactions);

    return clientMatters
        .filter(matter => {
            // Check if any trust transaction affects this matter's trust
            return trustTxs.some(tx => txAffectsCaseTrust(tx, matter.matterId));
        })
        .map(matter => {
            const matterName = matter.status === 'D' ? `${matter.name} (deleted)` : matter.name;
            return {
                matterId: matter.matterId,
                matterName,
                matterCode: matter.code,
                fullName: `${matterName} (${matter.code})`,
            };
        });
}

/**
 * Find all clients that have general trust activity.
 * For the Trust Ledger by Client report, this is typically just the selected client,
 * but a client may have multiple general trust accounts if they're involved in multiple matters.
 */
export function getClientTrustGroupsForClient(
    clientId: uuid,
    transactions: Transaction[],
    contacts: ContactLookup
): ClientTrustGroup[] {
    const trustTxs = filterTrustTransactions(transactions);

    // Check if this client has any general trust transactions
    const hasClientTrust = trustTxs.some(tx => txAffectsClientTrust(tx, clientId));

    if (!hasClientTrust) return [];

    const contact = contacts.get(clientId);
    const clientName = contact
        ? `${contact.first ?? ''} ${contact.last ?? ''}`.trim() || clientId
        : clientId;

    return [{
        clientId,
        clientName,
    }];
}

// ============================================================================
// Trust Ledger Data Calculation
// ============================================================================

/**
 * Calculate complete trust ledger data for a specific client.
 * Returns all trust transactions with running balances for each case trust and client trust.
 *
 * @param clientId - The client to generate the ledger for
 * @param transactions - All transactions from the store
 * @param matters - All matters from the store
 * @param contacts - All contacts from the store
 */
export function calculateTrustLedgerForClient(
    clientId: uuid,
    transactions: Transaction[],
    matters: Matter[],
    contacts: ContactLookup,
    scopedUserId: uuid | null = null
): {
    rows: TrustLedgerRow[];
    caseTrustGroups: CaseTrustGroup[];
    clientTrustGroups: ClientTrustGroup[];
    summary: TrustLedgerSummary;
} {
    // Get groups
    const scopedMatters = scopedUserId === null ? matters : matters.filter(m => m.assignedTo?.[0] === scopedUserId);
    const caseTrustGroups = getCaseTrustGroupsForClient(clientId, scopedMatters, transactions, contacts);
    const clientTrustGroups = scopedUserId === null ? getClientTrustGroupsForClient(clientId, transactions, contacts) : [];

    // Get all matter IDs for this client (to filter transactions)
    const clientMatterIds = new Set(caseTrustGroups.map(g => g.matterId));
    const clientIds = new Set(clientTrustGroups.map(g => g.clientId));

    // Filter to trust transactions that affect this client's trusts
    const relevantTxs = filterTrustTransactions(transactions).filter(tx => {
        // Check case trusts
        for (const matterId of clientMatterIds) {
            if (txAffectsCaseTrust(tx, matterId)) return true;
        }
        // Check client trusts
        for (const cid of clientIds) {
            if (txAffectsClientTrust(tx, cid)) return true;
        }
        return false;
    });

    // Sort by date ascending (oldest first for running balance)
    const sortedTxs = [...relevantTxs].sort((a, b) => {
        const dateA = new Date(a.txDate).getTime();
        const dateB = new Date(b.txDate).getTime();
        if (dateA !== dateB) return dateA - dateB;
        // Secondary sort by addedOn for same-day transactions
        return (a.addedOn || 0) - (b.addedOn || 0);
    });

    // Initialize running balances
    const caseTrustBalances = new Map<uuid, number>();
    caseTrustGroups.forEach(g => caseTrustBalances.set(g.matterId, 0));

    const clientTrustBalances = new Map<uuid, number>();
    clientTrustGroups.forEach(g => clientTrustBalances.set(g.clientId, 0));

    // Initialize summary totals
    const caseTrustTotals = new Map<uuid, {
        openingBalance: number;
        totalReceipts: number;
        totalDisbursements: number;
        closingBalance: number;
    }>();
    caseTrustGroups.forEach(g => caseTrustTotals.set(g.matterId, {
        openingBalance: 0,
        totalReceipts: 0,
        totalDisbursements: 0,
        closingBalance: 0,
    }));

    const clientTrustTotals = new Map<uuid, {
        openingBalance: number;
        totalReceipts: number;
        totalDisbursements: number;
        closingBalance: number;
    }>();
    clientTrustGroups.forEach(g => clientTrustTotals.set(g.clientId, {
        openingBalance: 0,
        totalReceipts: 0,
        totalDisbursements: 0,
        closingBalance: 0,
    }));

    // Build ledger rows
    const rows: TrustLedgerRow[] = sortedTxs.map(tx => {
        const rowCaseTrustReceipts = new Map<uuid, number>();
        const rowCaseTrustDisbursements = new Map<uuid, number>();
        const rowCaseTrustBalances = new Map<uuid, number>();

        const rowClientTrustReceipts = new Map<uuid, number>();
        const rowClientTrustDisbursements = new Map<uuid, number>();
        const rowClientTrustBalances = new Map<uuid, number>();

        // Calculate for each case trust
        for (const group of caseTrustGroups) {
            const receipts = calculateCaseTrustReceipts(tx, group.matterId);
            const disbursements = calculateCaseTrustDisbursements(tx, group.matterId);

            // Update running balance
            const currentBalance = caseTrustBalances.get(group.matterId) || 0;
            const newBalance = sumTrustMoney([currentBalance, receipts, -disbursements]);
            caseTrustBalances.set(group.matterId, newBalance);

            // Store in row
            rowCaseTrustReceipts.set(group.matterId, receipts);
            rowCaseTrustDisbursements.set(group.matterId, disbursements);
            rowCaseTrustBalances.set(group.matterId, newBalance);

            // Update totals
            const totals = caseTrustTotals.get(group.matterId)!;
            totals.totalReceipts = sumTrustMoney([totals.totalReceipts, receipts]);
            totals.totalDisbursements = sumTrustMoney([totals.totalDisbursements, disbursements]);
            totals.closingBalance = newBalance;
        }

        // Calculate for each client trust
        for (const group of clientTrustGroups) {
            const receipts = calculateClientTrustReceipts(tx, group.clientId);
            const disbursements = calculateClientTrustDisbursements(tx, group.clientId);

            // Update running balance
            const currentBalance = clientTrustBalances.get(group.clientId) || 0;
            const newBalance = sumTrustMoney([currentBalance, receipts, -disbursements]);
            clientTrustBalances.set(group.clientId, newBalance);

            // Store in row
            rowClientTrustReceipts.set(group.clientId, receipts);
            rowClientTrustDisbursements.set(group.clientId, disbursements);
            rowClientTrustBalances.set(group.clientId, newBalance);

            // Update totals
            const totals = clientTrustTotals.get(group.clientId)!;
            totals.totalReceipts = sumTrustMoney([totals.totalReceipts, receipts]);
            totals.totalDisbursements = sumTrustMoney([totals.totalDisbursements, disbursements]);
            totals.closingBalance = newBalance;
        }

        return {
            transactionId: tx.transactionId,
            txDate: tx.txDate,
            txType: tx.txType,
            txTypeName: getTxTypeName(tx.txType),
            referenceNumber: tx.referenceNumber ?? null,
            description: tx.notes ?? null,
            caseTrustReceipts: rowCaseTrustReceipts,
            caseTrustDisbursements: rowCaseTrustDisbursements,
            caseTrustBalances: rowCaseTrustBalances,
            clientTrustReceipts: rowClientTrustReceipts,
            clientTrustDisbursements: rowClientTrustDisbursements,
            clientTrustBalances: rowClientTrustBalances,
        };
    });

    const summary: TrustLedgerSummary = {
        caseTrustTotals,
        clientTrustTotals,
        transactionCount: rows.length,
    };

    return {
        rows,
        caseTrustGroups,
        clientTrustGroups,
        summary,
    };
}

// ============================================================================
// Exports for Transactions.tsx compatibility
// ============================================================================

/**
 * These functions provide the same interface as the methods in Transactions.tsx
 * so that component can be refactored to use this shared module.
 */

export {
    filterTrustTransactions as getClientTrustTransactions,
};
